import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

export const DATABRICKS_AGENT_SERVICES_API = "/api/2.1/unity-catalog/agent-services";
export const DATABRICKS_AGENT_SERVICE_SECURABLE = "AGENT_SERVICE";

export type DatabricksAgentServicePrivilege =
  | "EXECUTE"
  | "READ_METADATA"
  | "MANAGE"
  | "MANAGE_ACCESS_CONTROL"
  | "ALL_PRIVILEGES";

export interface DatabricksAgentServiceConnection {
  name: string;
}

export interface DatabricksAgentServiceConfig {
  connection: DatabricksAgentServiceConnection;
  base_path?: string;
  system_prompt?: string;
}

export interface DatabricksAgentService {
  name: string;
  full_name?: string | null;
  owner?: string | null;
  agent_service_type: "AGENT_SERVICE_TYPE_EXTERNAL";
  comment?: string;
  created_at?: number;
  created_by?: string;
  updated_at?: number;
  updated_by?: string;
  config: DatabricksAgentServiceConfig;
}

export interface DatabricksAgentServiceList {
  agent_services?: DatabricksAgentService[];
  next_page_token?: string;
}

export interface DatabricksAgentServiceCreateOptions {
  catalog: string;
  schema: string;
  id: string;
  /** Unity Catalog HTTP connection as `name`, `catalog.schema.name`, or either form prefixed by `connections/`. */
  connection: string;
  /** Path on the external service. Fabric Harness Apps expose `/responses`. */
  basePath?: string;
  comment?: string;
  systemPrompt?: string;
}

export interface DatabricksAgentServiceUpdateOptions {
  comment?: string;
  basePath?: string;
  systemPrompt?: string;
}

export interface DatabricksAgentServicePermission {
  principal?: string;
  privileges?: Array<{
    privilege?: DatabricksAgentServicePrivilege;
    inherited_from_object?: string[];
  }>;
}

export interface DatabricksAgentServicePermissions {
  privilege_assignments?: DatabricksAgentServicePermission[];
}

export interface DatabricksAgentServiceGrantChange {
  principal: string;
  add?: DatabricksAgentServicePrivilege[];
  remove?: DatabricksAgentServicePrivilege[];
}

export interface DatabricksAgentServicesOptions {
  /**
   * Agent Services is a Databricks Beta. Requiring this flag prevents preview
   * APIs from being enabled accidentally in production configuration.
   */
  acknowledgeBeta: true;
}

/**
 * Typed lifecycle client for Unity Catalog Agent Services.
 *
 * Databricks currently supports registration, discovery and grants only. This
 * client intentionally has no invoke method until runtime invocation is part of
 * the Agent Services API.
 */
export class DatabricksAgentServices {
  constructor(
    private readonly client: DatabricksRawProtocolClient,
    options: DatabricksAgentServicesOptions,
  ) {
    if (options.acknowledgeBeta !== true) {
      throw new Error("databricks agent services: acknowledgeBeta must be true.");
    }
  }

  create(options: DatabricksAgentServiceCreateOptions): Promise<DatabricksAgentService> {
    const catalog = ucIdentifier(options.catalog, "catalog");
    const schema = ucIdentifier(options.schema, "schema");
    const id = ucIdentifier(options.id, "agent service id");
    const basePath = externalBasePath(options.basePath ?? "/responses");
    return this.client.post<DatabricksAgentService>(
      DATABRICKS_AGENT_SERVICES_API,
      {
        agent_service_type: "AGENT_SERVICE_TYPE_EXTERNAL",
        ...(options.comment !== undefined ? { comment: options.comment } : {}),
        config: {
          connection: { name: connectionResourceName(options.connection) },
          base_path: basePath,
          ...(options.systemPrompt !== undefined ? { system_prompt: options.systemPrompt } : {}),
        },
      },
      { parent: `schemas/${catalog}.${schema}`, agent_service_id: id },
      { retry: "never" },
    );
  }

  get(fullName: string): Promise<DatabricksAgentService> {
    return this.client.get<DatabricksAgentService>(agentServicePath(fullName));
  }

  list(
    options: { catalog?: string; schema?: string; pageToken?: string } = {},
  ): Promise<DatabricksAgentServiceList> {
    if ((options.catalog === undefined) !== (options.schema === undefined)) {
      throw new Error("databricks agent services: catalog and schema must be provided together.");
    }
    const parent =
      options.catalog && options.schema
        ? `schemas/${ucIdentifier(options.catalog, "catalog")}.${ucIdentifier(options.schema, "schema")}`
        : undefined;
    return this.client.get<DatabricksAgentServiceList>(DATABRICKS_AGENT_SERVICES_API, {
      parent,
      page_token: options.pageToken,
    });
  }

  update(
    fullName: string,
    options: DatabricksAgentServiceUpdateOptions,
  ): Promise<DatabricksAgentService> {
    const mask: string[] = [];
    const config: Record<string, string> = {};
    const body: Record<string, unknown> = {};
    if (options.comment !== undefined) {
      mask.push("comment");
      body.comment = options.comment;
    }
    if (options.basePath !== undefined) {
      mask.push("config.base_path");
      config.base_path = externalBasePath(options.basePath);
    }
    if (options.systemPrompt !== undefined) {
      mask.push("config.system_prompt");
      config.system_prompt = options.systemPrompt;
    }
    if (mask.length === 0) {
      throw new Error(
        "databricks agent services: update requires comment, basePath or systemPrompt.",
      );
    }
    if (Object.keys(config).length > 0) body.config = config;
    return this.client.patch<DatabricksAgentService>(
      agentServicePath(fullName),
      body,
      { update_mask: mask.join(",") },
      { retry: "safe" },
    );
  }

  delete(fullName: string): Promise<void> {
    return this.client.delete<void>(agentServicePath(fullName), undefined, {}, { retry: "never" });
  }

  permissions(fullName: string): Promise<DatabricksAgentServicePermissions> {
    return this.client.get<DatabricksAgentServicePermissions>(permissionsPath(fullName));
  }

  updatePermissions(
    fullName: string,
    changes: DatabricksAgentServiceGrantChange[],
  ): Promise<DatabricksAgentServicePermissions> {
    if (changes.length === 0) {
      throw new Error("databricks agent services: at least one permission change is required.");
    }
    for (const change of changes) {
      if (!change.principal.trim())
        throw new Error("databricks agent services: principal cannot be empty.");
      if ((change.add?.length ?? 0) === 0 && (change.remove?.length ?? 0) === 0) {
        throw new Error(
          `databricks agent services: ${change.principal} has no privileges to add or remove.`,
        );
      }
    }
    return this.client.patch<DatabricksAgentServicePermissions>(
      permissionsPath(fullName),
      { changes },
      {},
      { retry: "safe" },
    );
  }

  grant(
    fullName: string,
    principal: string,
    privileges: DatabricksAgentServicePrivilege[] = ["EXECUTE"],
  ): Promise<DatabricksAgentServicePermissions> {
    return this.updatePermissions(fullName, [{ principal, add: privileges }]);
  }

  revoke(
    fullName: string,
    principal: string,
    privileges: DatabricksAgentServicePrivilege[] = ["EXECUTE"],
  ): Promise<DatabricksAgentServicePermissions> {
    return this.updatePermissions(fullName, [{ principal, remove: privileges }]);
  }
}

export function databricksAgentServices(
  client: DatabricksRawProtocolClient,
  options: DatabricksAgentServicesOptions,
): DatabricksAgentServices {
  return new DatabricksAgentServices(client, options);
}

function agentServicePath(fullName: string): string {
  return `${DATABRICKS_AGENT_SERVICES_API}/${encodeURIComponent(fullUcName(fullName))}`;
}

function permissionsPath(fullName: string): string {
  return `/api/2.1/unity-catalog/permissions/${DATABRICKS_AGENT_SERVICE_SECURABLE}/${encodeURIComponent(fullUcName(fullName))}`;
}

function fullUcName(value: string): string {
  const parts = value.split(".");
  if (parts.length !== 3) {
    throw new Error(
      `databricks agent services: expected catalog.schema.service, received ${value}.`,
    );
  }
  return parts
    .map((part, index) =>
      ucIdentifier(part, ["catalog", "schema", "service"][index] ?? "identifier"),
    )
    .join(".");
}

function connectionResourceName(value: string): string {
  const trimmed = value.trim();
  const name = trimmed.startsWith("connections/") ? trimmed.slice("connections/".length) : trimmed;
  const parts = name.split(".");
  if (parts.length !== 1 && parts.length !== 3) {
    throw new Error(
      `databricks agent services: expected connection name or catalog.schema.connection, received ${value}.`,
    );
  }
  return `connections/${parts
    .map((part, index) =>
      ucIdentifier(
        part,
        parts.length === 1
          ? "connection"
          : (["catalog", "schema", "connection"][index] ?? "identifier"),
      ),
    )
    .join(".")}`;
}

function externalBasePath(value: string): string {
  const path = value.trim();
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("?") || path.includes("#")) {
    throw new Error(
      "databricks agent services: basePath must be an absolute URL path without a query or fragment.",
    );
  }
  return path;
}

function ucIdentifier(value: string, label: string): string {
  const normalized = value.trim();
  if (!/^[A-Za-z0-9_][A-Za-z0-9_$-]*$/.test(normalized)) {
    throw new Error(`databricks agent services: invalid ${label} ${JSON.stringify(value)}.`);
  }
  return normalized;
}
